import type {
  Data,
  ContentComponentConfig,
  ContentRootProps,
  Citation,
  Excursion,
  ExcursionSmall,
  GlossaryItem,
  PageVersion,
  PageMeta,
  Outline,
  Figure,
} from "@op/puck-types";
import {
  Citations,
  Excursions,
  Glossary,
  ContentRenderer as PuckRenderer,
} from "@op/puck";
import {
  ContentProvider,
  ExcursionContextProvider,
  TranslationProvider,
} from "@op/utils";
import { useTranslations } from "../utils/translations";

export interface RendererProps {
  content: Data<ContentComponentConfig, ContentRootProps>;
  excursions: Excursion[];
  citations: { content: Citation[]; excursions: Record<string, Citation[]> };
  glossaryItems: GlossaryItem[];
  versions: PageVersion[];
  outline: { content: Outline; excursions: Record<string, Outline> };
  meta: PageMeta;
  figures: { content: Figure[]; excursions: Record<string, Figure[]> };
  pdfUrl?: string;
}

export const Renderer = ({
  content,
  excursions,
  citations,
  glossaryItems,
  versions,
  outline,
  meta,
  figures,
  pdfUrl,
}: RendererProps) => {
  const smallExcursions: ExcursionSmall[] = excursions.map((excursion) => ({
    id: excursion.id,
    overline: excursion.overline,
    title: excursion.title,
    slug: excursion.slug,
  }));

  const translations = useTranslations();

  return (
    <ContentProvider
      meta={meta}
      citations={citations}
      glossaryItems={glossaryItems}
      excursions={smallExcursions}
      versions={versions}
      outline={outline}
      figures={figures}
      pdfUrl={pdfUrl}
    >
      <TranslationProvider {...translations}>
        <ExcursionContextProvider>
          <PuckRenderer data={content} />
          <Excursions excursions={excursions} />
          <Glossary />
          <Citations />
        </ExcursionContextProvider>
      </TranslationProvider>
    </ContentProvider>
  );
};
